import { Button, CloseButton, Modal, Text, createStyles } from '@mantine/core'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { FC, ReactNode } from 'react'
import { ChevronRightIcon } from '@heroicons/react/24/solid'
import ImageComponent from '@/components/ImageComponent'

type TPopupContentTemplate = {
  headerImage: string
  statusText: string
  description: string
  buttonText: string | ReactNode
  buttonOnClick: () => void
}

const PopupContentTemplate: FC<TPopupContentTemplate> = ({
  headerImage,
  statusText,
  description,
  buttonOnClick,
  buttonText,
}) => {
  return (
    <div className="flex flex-col items-center px-4">
      <div className="w-full my-2">
        <ImageComponent className="w-full h-auto" src={headerImage} />
      </div>
      <div className="mt-1">
        <Text c="dimmed" size="md">
          {statusText}
        </Text>
      </div>
      <div className="mt-2">
        <Text size="lg">{description}</Text>
      </div>
      <div className="mt-8 mb-4">
        <Button
          size="md"
          onClick={() => {
            buttonOnClick()
          }}
        >
          {buttonText}
        </Button>
      </div>
    </div>
  )
}

const useStyles = createStyles(() => ({
  close: { display: 'none' },
  header: {
    display: 'none',
  },
  overlay: {
    background: `rgba(0, 0, 0, 0.40) !important`,
  },
  modal: {
    borderRadius: '8px',
    paddingTop: '24px !important',
    paddingLeft: '16px !important',
    paddingRight: '16px !important',
  },
}))

export type TRedeemStatusPopup = {
  status: 'success' | 'fail'
  errorMessage?: string
  open?: boolean
  onClose?: (status: TRedeemStatusPopup['status']) => void
}

const RedeemStatusPopup: FC<TRedeemStatusPopup> = ({
  status,
  errorMessage,
  open = false,
  onClose,
}) => {
  const { t } = useTranslation()
  const router = useRouter()

  const { classes } = useStyles()
  const handleClose = () => {
    onClose?.(status)
  }

  return (
    <Modal
      centered
      opened={open}
      onClose={handleClose}
      classNames={classes}
      closeOnEscape={false}
      closeOnClickOutside={false}
    >
      {status === 'success' && (
        <PopupContentTemplate
          headerImage={
            'https://res.gengesports.cn/user-web/images/key-claim/success-header.png'
          }
          statusText={`${t('amazing')}!`}
          description={t('redeem-success-message')}
          buttonText={
            <span className={'flex items-center'}>
              <span>{t('purchase')}</span>
              <ChevronRightIcon className={'inline-block h-4 w-4'} />
            </span>
          }
          buttonOnClick={() => {
            router.push('/course-center', undefined, {
              shallow: true,
            })
          }}
        />
      )}
      {status === 'fail' && (
        <PopupContentTemplate
          headerImage="https://res.gengesports.cn/user-web/images/key-claim/Error.png"
          statusText={`${t('error')}!`}
          description={
            errorMessage || t('server_response.error.redeem.generic')
          }
          buttonText={t('try-again')}
          buttonOnClick={() => {
            handleClose()
          }}
        />
      )}
    </Modal>
  )
}

export default RedeemStatusPopup
